import type { QueryClient } from "@tanstack/react-query";
import {
  collection,
  getDocsFromServer,
  query,
  where,
} from "firebase/firestore";
import { db } from "@/Utils/firebase";
import { getAvatarFromLocalStorage } from "@/Utils";

/**
 * The avatars list, as the app keeps it: read once, held in the react-query
 * cache for the life of the page, and shared by AvatarScreen and AvatarSelect.
 *
 * The Firestore SDK cannot be that cache. Its memory cache is eager-GC: it
 * drops a query's documents the moment the query's last listener goes, so a
 * list kept only there is gone every time the user leaves the screen, and
 * Settings → Back waited for the server (BUG-0014; TRAPS.md).
 */
export interface ScanListDoc {
  id: string;
  pipelineStatus?: string;
  /** Seconds, set by `ScanModel.create` when the scan is submitted. */
  timestamp?: number;
  [field: string]: unknown;
}

export const scansListKey = (uid: string) => ["scans", uid] as const;

/**
 * How long a loaded list is shown without asking the server again. A return
 * inside this makes no call at all; after it, the cached list still shows at
 * once and one background refetch brings it up to date. Scans still being
 * processed do not depend on it: each is watched until it finishes.
 */
export const SCANS_LIST_STALE_MS = 2 * 60 * 1000;

/**
 * How long a first load (nothing cached) keeps the full-screen spinner up.
 * The same 10 s as the share-link splash (`SPLASH_READ_BUDGET_MS`), whose
 * healthy loads measured p99 13.1 s for up to four reads. After it the screen
 * is usable and only the list area waits.
 */
export const SCANS_LIST_BUDGET_MS = 10_000;

/**
 * A scan older than this is not "just submitted", finished or not. Healthy
 * scans finish in minutes (prod, last 30 days: creation → last write p50 2 min,
 * p90 5.4 min); the scans that never finish are all legacy (264 in prod, the
 * newest 315 days old), and without a bound each of their owners' visits would
 * watch up to 20 of them for nothing.
 */
export const JUST_SUBMITTED_WINDOW_SECONDS = 24 * 60 * 60;

/** "Approved or rejected": the two states a scan does not leave. */
export const isFinishedStatus = (status: unknown): boolean =>
  status === "APPROVED" || status === "FAILURE";

/** A scan the list should watch until it finishes. */
export const isJustSubmitted = (doc: ScanListDoc, nowSeconds: number): boolean =>
  !isFinishedStatus(doc.pipelineStatus) &&
  typeof doc.timestamp === "number" &&
  nowSeconds - doc.timestamp < JUST_SUBMITTED_WINDOW_SECONDS;

/**
 * Put one scan into the user's cached list: replace its copy, or add it.
 *
 * Only ever into a list that is already there. A list made from one scan would
 * render as the user's whole list and hide every other avatar; with nothing
 * cached, the next load brings the scan in anyway.
 */
export const upsertScanInList = (
  client: QueryClient,
  uid: string,
  doc: ScanListDoc,
): void => {
  client.setQueryData<ScanListDoc[]>(scansListKey(uid), (list) => {
    if (!list) return list;
    return list.some((cached) => cached.id === doc.id)
      ? list.map((cached) => (cached.id === doc.id ? doc : cached))
      : [...list, doc];
  });
};

/**
 * Load the user's scans from the SERVER.
 *
 * `getDocs` would resolve with the SDK's memory cache once the client decides
 * it is offline (10 s after a connection starts with no answer). After eager
 * GC that cache is empty, or holds only the scans a watcher pins, so the list
 * would be cached as empty or partial. `getDocsFromServer` rejects instead,
 * and the rejection is left alone so react-query retries it.
 */
export const fetchScansList = async (uid: string): Promise<ScanListDoc[]> => {
  const snapshot = await getDocsFromServer(
    query(collection(db, "scans"), where("userId", "==", uid)),
  );
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

/** The submission this device is making, from `localStorage`. */
export interface InFlightSubmission {
  scanId: string;
  /** Seconds, when the upload started. */
  timestamp: number;
  name: string;
}

export const readInFlightSubmission = (): InFlightSubmission | null => {
  const scanId = localStorage.getItem("scanID");
  const timestamp = localStorage.getItem("timestamp");
  if (!scanId || !timestamp) return null;
  const newAvatar = getAvatarFromLocalStorage();
  return {
    scanId,
    timestamp: parseInt(timestamp),
    name: `${newAvatar?.firstName || ""} ${newAvatar?.lastName || ""}`,
  };
};
